import DivLoader from "components/divloader/DivLoader";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { SketchPicker } from "react-color";
import sampleImage from "../../../../assets/img/payment-clients/placeholder.png";
import { createUpdateCheckout, getCheckOutDetails, updateCheckoutStatus } from "api/clients";
import ShortTruncateCopy from "components/common/RegistrationLinkCopy";
import InputField from "components/fields/InputField";
import { getClient } from "api/graphQL_clients";


const smart_routingimg = require("../../../../assets/svg/smart_routing.svg");

const CheckoutProductClientModal = ({
  id, secret, fetchClients, assigned_payment_methods, infoData,
}: {
  id: string; secret: string; fetchClients: () => void; assigned_payment_methods?: any; infoData?: any;
}) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState<boolean>(false);
  // const [clientData] = useState(infoData?.row?.original ?? {});
  const [clientData, setClientData] = useState<any>({});
  const [brandColor, setBrandColor] = useState("#2020e4");
  const [style, setStyle] = useState("rounded");
  const [selectedFont, setSelectedFont] = useState("'Poppins'");
  const [isCheckoutEnabled, setIsCheckoutEnabled] = useState(false);
  // const [checkOutStatus, setCheckOutStatus] = useState(infoData?.row?.original?.is_checkout_enabled || false);
  const [checkOutStatus, setCheckOutStatus] = useState<boolean | null>(null);
  const [businessName, setBusinessName] = useState("Business Name");
  const [imagePreview, setImagePreview] = useState(sampleImage);
  const [base64Image, setBase64Image] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#2020e4");
  const [secondaryColor, setSecondaryColor] = useState("#422AFB");
  const [buttonColor, setButtonColor] = useState("#5757ac");
  const [showPrimaryPicker, setShowPrimaryPicker] = useState(false);
  const [showSecondaryPicker, setShowSecondaryPicker] = useState(false);
  const [showButtonPicker, setShowButtonPicker] = useState(false);
  const generatedLink = "https://" + window.location.hostname + "/checkout/" + id + "/" + secret;

  function fetchClient() {
    setInitialLoading(true);
    getClient(infoData?.row?.original?.client_id)
      .then((data: any) => {
        setClientData(data[0] ?? {});
      })
      .catch((err: any) => toast.error(err?.response?.data?.message ?? "Unable to fetch data"))
      .finally(() => setIsLoading(false));
  }
  useEffect(() => {
    fetchClient()
  },[])
  useEffect(() => {
    if (!clientData?.client_id) return;
    setInitialLoading(true);
    getCheckOutDetails(clientData?.checkout_id, clientData?.client_id, clientData?.client_secret)
      .then((d) => {
        const r = d?.data;
        if (r?.brand_color) setBrandColor(r.brand_color);
        if (r?.primary_color) setPrimaryColor(r.primary_color);
        if (r?.button_color) setButtonColor(r.button_color);
        if (r?.secondary_color) setSecondaryColor(r.secondary_color);
        if (r?.brand_style) setStyle(r.brand_style);
        if (r?.brand_font) setSelectedFont(r.brand_font);
        if (r?.business_name) setBusinessName(r.business_name);
        if (r?.business_logo) { setImagePreview("data:image/jpeg;base64," + r.business_logo); setBase64Image(r.business_logo); }
        setCheckOutStatus(r?.is_checkout_enabled ?? false);
      })
      .catch(() => {
        setCheckOutStatus(false);
        // toast.error("Error fetching checkout data");
      })
      .finally(() => {
        setInitialLoading(false);
      });
  }, [clientData]);

  const getBorderRadiusClass = () => style === "sharp" ? "rounded-none" : style === "pill" ? "rounded-[25px]" : "rounded-[10px]";

  const handleToggle = () => {
    setIsCheckoutEnabled(!isCheckoutEnabled);
    setIsLoading(true);
    updateCheckoutStatus({ status: !isCheckoutEnabled }, clientData?.client_id, clientData?.client_secret)
      .then(() => setCheckOutStatus(!checkOutStatus))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleFileChange = (e: any) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
    if (!allowed.includes(file.type)) { toast.error("Only JPEG, PNG, GIF allowed."); e.target.value = ""; return; }
    if (file.size > 1 * 1024 * 1024) { toast.error("File must be < 1MB."); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = () => { const b64 = reader.result as string; setImagePreview(b64); setBase64Image(b64.split(",")[1]); };
  };

  const handleSubmit = () => {
    setIsLoading(true);
    createUpdateCheckout({ brand_color: brandColor, brand_style: style, brand_font: selectedFont, status: isCheckoutEnabled, business_name: businessName, business_logo: base64Image, primary_color: primaryColor, secondary_color: secondaryColor, button_color: buttonColor }, clientData?.client_id, clientData?.client_secret)
      .then((r) => { toast.success(r?.message); fetchClients(); })
      .catch((err) => toast.error(err?.response?.data?.message ?? "Failed to update"))
      .finally(() => setIsLoading(false));
  };

  if (initialLoading || checkOutStatus === null) {
    return (
      <>
        {(initialLoading || checkOutStatus === null) ? (
          <div className="cco-loading">
            <span className="cco-spinner-lg"></span>
            <p className="cco-loading-txt">Loading checkout settings…</p>
          </div>
        ) : (
          <div className="cco-root">
            ...
          </div>
        )}
        <style>{`
          .cco-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:48px;gap:14px;}
          .cco-spinner-lg {width:26px;height:26px;border:3px solid #e2e8f0;border-top-color:#2563eb;border-radius:50%;animation: ccoSpinLg .7s linear infinite;}
          @keyframes ccoSpinLg {to { transform: rotate(360deg); }}
          .cco-loading-txt {font-size:13.5px;color:#94a3b8;font-weight:500;}
        `}</style>
      </>
    );
  }

  return (
    <div className="cco-root">
      {/* Header */}
      <div className="cco-page-header">
        <div className="cco-header-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
            <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
          </svg>
        </div>
        <div className="cco-header-info">
          <h3 className="cco-title">Checkout Settings</h3>
          <p className="cco-subtitle">Configure your client's hosted checkout experience</p>
        </div>
        {/* Enable toggle */}
        <label className="cco-toggle">
          <input type="checkbox" className="cco-toggle-inp" checked={isCheckoutEnabled} onChange={handleToggle} />
          <div className="cco-toggle-track"><div className="cco-toggle-thumb" /></div>
          <span className={`cco-toggle-lbl${isCheckoutEnabled ? " cco-toggle-lbl--on" : ""}`}>{isCheckoutEnabled ? "Enabled" : "Disabled"}</span>
        </label>
      </div>

      {/* Checkout link */}
      {checkOutStatus && (
        <div className="cco-link-card">
          <div className="cco-link-icon">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
          </div>
          <div className="cco-link-body">
            <p className="cco-link-label">Checkout URL</p>
            <ShortTruncateCopy info={generatedLink} showCopy={true} />
          </div>
        </div>
      )}

      {checkOutStatus ? (
        <div className="cco-form-area">

          {/* Business Section */}
          <div className="cco-section">
            <div className="cco-section-header">
              <div className="cco-section-icon cco-section-icon--blue">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              </div>
              Business Information
            </div>
            <div className="cco-2col">
              <div className="cco-fields">
                <InputField variant="auth" extra="w-full" label="Business Name" placeholder="Enter business name" id="business_name" type="text" value={businessName} onChange={(e: any) => setBusinessName(e.target.value)} />
                <InputField variant="auth" extra="w-full" label="Business Logo" placeholder="Choose logo" id="business_logo" type="file" onChange={handleFileChange} />
              </div>
              <div>
                <label className="cco-label">Logo Preview</label>
                <div className="cco-logo-preview">
                  {imagePreview ? <img src={imagePreview} alt="Logo preview" className="cco-logo-img" /> : <span className="cco-logo-empty">No logo selected</span>}
                </div>
              </div>
            </div>
          </div>

          {/* Style Section */}
          <div className="cco-section">
            <div className="cco-section-header">
              <div className="cco-section-icon cco-section-icon--purple">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="13.5" cy="6.5" r="2.5"/><path d="M17.5 3H21v3.5"/><path d="M3 10.5v10a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-10"/><path d="M3 10.5L12 3l4 3.5"/></svg>
              </div>
              Style Configuration
            </div>
            <div className="cco-2col">
              <div className="cco-field">
                <label className="cco-label">Border Style</label>
                <select className="cco-select" onChange={(e) => setStyle(e.target.value)} value={style}>
                  <option value="rounded">Rounded</option>
                  <option value="sharp">Sharp</option>
                  <option value="pill">Pill</option>
                </select>
                <div className="cco-style-preview">
                  <span className="cco-style-preview-label">Preview</span>
                  <input type="text" className={`border-2 p-2.5 w-full ${getBorderRadiusClass()} border-blue-400 bg-white text-gray-700 text-sm`} disabled value="Input Preview" />
                </div>
              </div>
              <div className="cco-field">
                <label className="cco-label">Font Family</label>
                <select className="cco-select" onChange={(e) => setSelectedFont(e.target.value)} value={selectedFont}>
                  <option value="'Poppins'">Poppins</option>
                  <option value="'DM Sans'">DM Sans</option>
                  <option value="'circular'">Circular</option>
                </select>
                <div className="cco-font-preview">
                  <span className="cco-style-preview-label">Preview</span>
                  <p style={{ fontFamily: selectedFont, fontSize: "18px" }} className="text-gray-800 font-medium">Font Preview Text</p>
                </div>
              </div>
            </div>
          </div>

          {/* Brand Colors */}
          <div className="cco-section">
            <div className="cco-section-header">
              <div className="cco-section-icon cco-section-icon--amber">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="m4.93 4.93 14.14 14.14"/></svg>
              </div>
              Brand Colors
            </div>
            <div className="cco-3col">
              {[
                { label: "Primary Color", color: primaryColor, setColor: setPrimaryColor, show: showPrimaryPicker, setShow: setShowPrimaryPicker },
                { label: "Secondary Color", color: secondaryColor, setColor: setSecondaryColor, show: showSecondaryPicker, setShow: setShowSecondaryPicker },
                { label: "Button Color", color: buttonColor, setColor: setButtonColor, show: showButtonPicker, setShow: setShowButtonPicker },
              ].map((c) => (
                <div key={c.label} className="cco-field">
                  <label className="cco-label">{c.label}</label>
                  <div className="cco-color-trigger" onClick={() => c.setShow(!c.show)}>
                    <div className="cco-color-swatch" style={{ background: c.color }} />
                    <span className="cco-color-hex">{c.color}</span>
                  </div>
                  {c.show && (
                    <div className="cco-picker-wrap">
                      <div className="cco-picker-overlay" onClick={() => c.setShow(false)} />
                      <SketchPicker color={c.color} onChange={(col) => c.setColor(col.hex)} disableAlpha />
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Gradient preview */}
            <div className="cco-gradient-preview" style={{ background: `linear-gradient(to right, ${primaryColor}, ${secondaryColor})` }}>
              <span className="cco-gradient-label">{businessName || "Business Name"}</span>
            </div>
            <div style={{ marginTop: "12px" }}>
              <button className="cco-preview-btn" style={{ background: buttonColor }}>Primary Button</button>
            </div>
          </div>

          {/* Submit */}
          <div className="cco-footer">
            <p className="cco-footer-note">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
              Changes apply to the live checkout immediately
            </p>
            <button className="cco-submit-btn" onClick={handleSubmit} disabled={!!isLoading}>
              {isLoading ? <span className="cco-spinner" /> : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Save Settings
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="cco-disabled-state">
          <img src={smart_routingimg.default} alt="Disabled" className="cco-disabled-img" loading="lazy" />
          <h3 className="cco-disabled-title">Checkout is disabled</h3>
          <p className="cco-disabled-desc">Enable the checkout toggle above to configure and customise your checkout settings.</p>
        </div>
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        .cco-root { font-family: 'Plus Jakarta Sans', sans-serif; display: flex; flex-direction: column; gap: 20px; }
        .cco-page-header { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
        .cco-header-icon { width: 44px; height: 44px; border-radius: 12px; background: linear-gradient(135deg,#faf5ff,#ede9fe); border: 1.5px solid #ddd6fe; display: flex; align-items: center; justify-content: center; color: #7c3aed; flex-shrink: 0; }
        .cco-header-info { flex: 1; }
        .cco-title { font-size: 16px; font-weight: 700; color: #0f172a; margin-bottom: 3px;line-height:22px }
        .cco-subtitle { font-size: 13px; color: #64748b; font-weight: 500; }
        /* Toggle */
        .cco-toggle { display: flex; align-items: center; gap: 8px; cursor: pointer; margin-left: auto; }
        .cco-toggle-inp { display: none; }
        .cco-toggle-track { width: 44px; height: 24px; background: #e2e8f0; border-radius: 100px; position: relative; transition: background .2s; flex-shrink: 0; }
        .cco-toggle-inp:checked ~ .cco-toggle-track { background: #2563eb; }
        .cco-toggle-thumb { position: absolute; top: 3px; left: 3px; width: 18px; height: 18px; border-radius: 50%; background: #fff; transition: transform .2s; box-shadow: 0 1px 4px rgba(0,0,0,.18); }
        .cco-toggle-inp:checked ~ .cco-toggle-track .cco-toggle-thumb { transform: translateX(20px); }
        .cco-toggle-lbl { font-size: 13px; font-weight: 600; color: #64748b; }
        .cco-toggle-lbl--on { color: #2563eb; }
        /* Link card */
        .cco-link-card { display: flex; align-items: center; gap: 12px; background: #f0fdf4; border: 1.5px solid #bbf7d0; border-radius: 12px; padding: 14px 18px; }
        .cco-link-icon { width: 34px; height: 34px; border-radius: 9px; background: #fff; border: 1.5px solid #bbf7d0; display: flex; align-items: center; justify-content: center; color: #16a34a; flex-shrink: 0; }
        .cco-link-body { flex: 1; min-width: 0; }
        .cco-link-label { font-size: 11px; font-weight: 700; color: #15803d; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 4px; }
        /* Form area */
        .cco-form-area { display: flex; flex-direction: column; gap: 16px; }
        .cco-section { background: #ffffff; border: 1px solid #e2e8f0; border-radius: 16px; padding: 20px; }
        .cco-section-header { display: flex; align-items: center; gap: 9px; font-size: 13px; font-weight: 700; color: #374151; text-transform: uppercase; letter-spacing: .4px; margin-bottom: 16px; }
        .cco-section-icon { width: 30px; height: 30px; border-radius: 8px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .cco-section-icon--blue { background: linear-gradient(135deg,#eff6ff,#dbeafe); border: 1.5px solid #bfdbfe; color: #2563eb; }
        .cco-section-icon--purple { background: linear-gradient(135deg,#faf5ff,#ede9fe); border: 1.5px solid #ddd6fe; color: #7c3aed; }
        .cco-section-icon--amber { background: linear-gradient(135deg,#fffbeb,#fef3c7); border: 1.5px solid #fde68a; color: #d97706; }
        .cco-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .cco-3col { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; }
        .cco-fields { display: flex; flex-direction: column; gap: 12px; }
        .cco-field { display: flex; flex-direction: column; gap: 7px; }
        .cco-label { font-size: 12.5px; font-weight: 600; color: #374151; }
        .cco-select { padding: 10px 13px; background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 10px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; color: #0f172a; outline: none; transition: border-color .18s; }
        .cco-select:focus { border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37,99,235,.1); }
        .cco-style-preview { margin-top: 8px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 10px; display: flex; flex-direction: column; gap: 6px; }
        .cco-font-preview { margin-top: 8px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 19px 10px; }
        .cco-style-preview-label { font-size: 10.5px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: .4px; }
        .cco-logo-preview { height: 130px; background: linear-gradient(135deg,#eff6ff,#f8fafc); border: 1.5px solid #e2e8f0; border-radius: 12px; display: flex; align-items: center; justify-content: center; overflow: hidden;margin-top:4px }
        .cco-logo-img { max-height: 100%; max-width: 100%; object-fit: contain; padding: 8px; }
        .cco-logo-empty { font-size: 12.5px; color: #94a3b8; font-weight: 500; }
        /* Color picker */
        .cco-color-trigger { display: flex; align-items: center; gap: 10px; padding: 10px 13px; background: #f8fafc; border: 1.5px solid #e2e8f0; border-radius: 10px; cursor: pointer; transition: border-color .15s; }
        .cco-color-trigger:hover { border-color: #93c5fd; }
        .cco-color-swatch { width: 28px; height: 28px; border-radius: 7px; border: 2px solid rgba(0,0,0,.08); flex-shrink: 0; }
        .cco-color-hex { font-family: 'Courier New', monospace; font-size: 13px; font-weight: 600; color: #374151; }
        .cco-picker-wrap { position: relative; z-index: 10; }
        .cco-picker-overlay { position: fixed; inset: 0; }
        /* Gradient preview */
        .cco-gradient-preview { height: 80px; border-radius: 12px; display: flex; align-items: center; justify-content: center; margin-top: 16px; }
        .cco-gradient-label { font-size: 18px; font-weight: 700; color: #fff; text-shadow: 0 1px 4px rgba(0,0,0,.2); }
        .cco-preview-btn { padding: 10px 22px; border-radius: 10px; border: none; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 14px; font-weight: 700; color: #fff; cursor: pointer; }
        /* Footer */
        .cco-footer { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 12px; padding: 16px 20px; background: #fafbfc; border: 1px solid #e2e8f0; border-radius: 14px; }
        .cco-footer-note { display: flex; align-items: center; gap: 6px; font-size: 12px; color: #94a3b8; font-weight: 500; }
        .cco-submit-btn { display: inline-flex; align-items: center; gap: 7px; font-family: 'Plus Jakarta Sans', sans-serif; font-size: 13.5px; font-weight: 700; color: #fff; padding: 11px 22px; border-radius: 10px; border: none; background: linear-gradient(135deg,#1d4ed8,#2563eb); cursor: pointer; box-shadow: 0 4px 14px rgba(37,99,235,.3); transition: all .15s; }
        .cco-submit-btn:hover { transform: translateY(-1px); box-shadow: 0 8px 24px rgba(37,99,235,.38); }
        .cco-submit-btn:disabled { opacity: .6; cursor: not-allowed; transform: none; }
        .cco-spinner { display: inline-block; width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.35); border-top-color: #fff; border-radius: 50%; animation: ccoSpin .65s linear infinite; }
        @keyframes ccoSpin { to { transform: rotate(360deg); } }
        /* Disabled */
        .cco-disabled-state { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 14px; padding: 48px 24px; background: #fff; border: 1px solid #e2e8f0; border-radius: 16px; text-align: center; }
        .cco-disabled-img { width: 180px; opacity: .7; }
        .cco-disabled-title { font-size: 17px; font-weight: 800; color: #64748b; }
        .cco-disabled-desc { font-size: 13.5px; color: #94a3b8; line-height: 1.6; max-width: 340px; }
        @media (max-width: 700px) { .cco-2col, .cco-3col { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
};

export default CheckoutProductClientModal;